import { useEffect, useState, type ReactNode } from "react";
import {
  BellRing,
  CheckCircle2,
  Clock3,
  Loader2,
  Mail,
  TriangleAlert,
  XCircle,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetPro014CreationJobStatusQuery } from "@/services/campaign-pro014";

const POLL_INTERVAL_MS = 3000;

const JOB_STATE = {
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

const STATE_LABEL: Record<string, string> = {
  waiting: "Na fila",
  delayed: "Na fila",
  active: "Processando itens",
  completed: "Concluída",
  failed: "Falhou",
};

interface Pro014JobProgress {
  status?: string;
  percent?: number;
  processedItems?: number;
  totalItems?: number;
}

interface QueuedCreationDialogProps {
  open: boolean;
  jobId: string | number | null;
  itemsCount: number;
  onClose: () => void;
}

export function QueuedCreationDialog({
  open,
  jobId,
  itemsCount,
  onClose,
}: QueuedCreationDialogProps): ReactNode {
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);

  const shouldSkip: boolean = !open || jobId === null;

  const { data } = useGetPro014CreationJobStatusQuery(jobId ?? 0, {
    skip: shouldSkip,
    pollingInterval: pollingEnabled ? POLL_INTERVAL_MS : 0,
  });

  const state: string = data?.state ?? "";
  const progress: Pro014JobProgress = (data?.progress ??
    {}) as Pro014JobProgress;
  const percent: number = progress.percent ?? 0;
  const isCompleted: boolean = state === JOB_STATE.COMPLETED;
  const isFailed: boolean = state === JOB_STATE.FAILED;
  const isTerminal: boolean = isCompleted || isFailed;

  useEffect(() => {
    setPollingEnabled(!isTerminal);
  }, [isTerminal]);

  const titleIcon: ReactNode = isCompleted ? (
    <CheckCircle2 className="h-5 w-5 text-green-600" />
  ) : isFailed ? (
    <XCircle className="h-5 w-5 text-red-600" />
  ) : (
    <Clock3 className="h-5 w-5 text-amber-600" />
  );

  const title: string = isCompleted
    ? "Campanha criada"
    : isFailed
      ? "Falha na criação"
      : "Criação em background";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {titleIcon}
            {title}
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-2">
            <span className="block">
              A campanha entrou em processamento assíncrono porque o volume de
              itens exige um fluxo mais seguro que a inserção em tempo real.
            </span>
            <span className="block">
              Você pode acompanhar o progresso aqui ou sair — ao concluir, a
              plataforma envia notificação e email.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 rounded-lg border bg-muted/40 p-4 text-sm">
          {isCompleted && (
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              <span>Campanha criada com sucesso!</span>
            </div>
          )}

          {isFailed && (
            <div className="grid gap-1 rounded border border-red-200 bg-red-50 p-2 text-red-700">
              <div className="flex items-center gap-2">
                <TriangleAlert className="h-4 w-4" />
                <span className="font-medium">Erro na criação</span>
              </div>
              <span className="text-sm">
                {data?.failedReason ?? "Falha desconhecida no processamento."}
              </span>
            </div>
          )}

          {!isTerminal && (
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  {STATE_LABEL[state] ?? "Processando itens"}
                </span>
                <span>{percent}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-primary" />
            <span>Você será avisado quando a inserção base terminar.</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <span>O email de conclusão será enviado automaticamente.</span>
          </div>

          {jobId !== null && (
            <div className="grid gap-1 pt-1 text-muted-foreground">
              <span>Job #{jobId}</span>
              <span>{itemsCount} item(ns) enviados para fila.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            onClick={onClose}
          >
            Ir para campanhas
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
